import React from "react";

interface BigFiveScoreDisplayProps {
  scores: Record<string, any>;
  interpretations: Record<string, any>;
}

const BigFiveScoreDisplay: React.FC<BigFiveScoreDisplayProps> = ({
  scores,
  interpretations,
}) => {
  console.log("scores", scores);
  // Define the Big Five dimensions
  const dimensions = [
    {
      key: "E",
      title: "Energía",
      description:
        "Evalúa aspectos relacionados con la extraversión, energía, dinamismo y dominancia",
      color: "blue",
    },
    {
      key: "A",
      title: "Afabilidad",
      description:
        "Evalúa aspectos relacionados con la cooperación, empatía y cordialidad",
      color: "green",
    },
    {
      key: "T",
      title: "Tesón",
      description:
        "Evalúa aspectos relacionados con la responsabilidad, perseverancia y orden",
      color: "purple",
    },
    {
      key: "EE",
      title: "Estabilidad Emocional",
      description:
        "Evalúa aspectos relacionados con el control emocional y la ansiedad",
      color: "yellow",
    },
    {
      key: "AM",
      title: "Apertura Mental",
      description:
        "Evalúa aspectos relacionados con la apertura a la cultura y experiencias",
      color: "orange",
    },
  ];

  // Helper function to get color classes based on dimension
  const getColorClasses = (color: string) => {
    switch (color) {
      case "blue":
        return {
          text: "text-blue-500",
          bg: "bg-blue-500",
          border: "border-blue-500",
        };
      case "green":
        return {
          text: "text-green-500",
          bg: "bg-green-500",
          border: "border-green-500",
        };
      case "purple":
        return {
          text: "text-purple-500",
          bg: "bg-purple-500",
          border: "border-purple-500",
        };
      case "yellow":
        return {
          text: "text-yellow-500",
          bg: "bg-yellow-500",
          border: "border-yellow-500",
        };
      case "orange":
        return {
          text: "text-orange-500",
          bg: "bg-orange-500",
          border: "border-orange-500",
        };
      default:
        return {
          text: "text-gray-500",
          bg: "bg-gray-500",
          border: "border-gray-500",
        };
    }
  };

  // Helper function to get interpretation level
  const getInterpretationLevel = (tScore: number) => {
    if (tScore <= 45) return "Bajo";
    if (tScore <= 55) return "Promedio";
    return "Alto";
  };

  // Helper function to get interpretation color
  const getInterpretationColor = (tScore: number) => {
    if (tScore <= 45) return "text-red-500";
    if (tScore <= 55) return "text-yellow-500";
    return "text-green-500";
  };

  // Helper function to get progress bar width
  const getProgressWidth = (tScore: number) => {
    // Convert T-score to percentage (T-scores typically range from 20 to 80)
    const percentage = ((tScore - 20) / 60) * 100;
    return Math.min(Math.max(percentage, 0), 100);
  };

  return (
    <div className="space-y-4">
      {/* Main dimensions */}
      <div className="grid grid-cols-1 gap-4">
        {dimensions.map((dimension) => {
          const score = scores[dimension.key];
          const interpretation = interpretations[dimension.key];
          const colorClasses = getColorClasses(dimension.color);
          const interpretationLevel = getInterpretationLevel(score.t_score);
          const interpretationColor = getInterpretationColor(score.t_score);

          return (
            <div
              key={dimension.key}
              className="rounded-lg border border-gray-300 bg-white p-4 dark:bg-navy-800"
            >
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-700 dark:text-white">
                  {dimension.title}
                </h3>
                <div className="flex items-center">
                  <span className="mr-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                    T = {score.t_score}
                  </span>
                  <span
                    className={`text-sm font-medium ${interpretationColor}`}
                  >
                    {interpretationLevel}
                  </span>
                </div>
              </div>

              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                {dimension.description}
              </p>

              <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-navy-700">
                <div
                  className={`h-2.5 rounded-full ${getInterpretationColor(
                    score.t_score
                  ).replace("text-", "bg-")}`}
                  style={{ width: `${getProgressWidth(score.t_score)}%` }}
                ></div>
              </div>

              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                <p>{interpretation}</p>
              </div>
            </div>
          );
        })}

        {/* Distortion scale */}
        <div className="rounded-lg border border-gray-300 bg-white p-4 dark:bg-navy-800">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-lg font-bold text-gray-700 dark:text-white">
              Escala de Distorsión
            </h3>
            <div className="flex items-center">
              <span className="mr-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                T = {scores.D.t_score}
              </span>
              <span
                className={`text-sm font-medium ${getInterpretationColor(
                  scores.D.t_score
                )}`}
              >
                {getInterpretationLevel(scores.D.t_score)}
              </span>
            </div>
          </div>

          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            {scores.D.description}
          </p>

          <div className="h-2.5 w-full rounded-full bg-gray-200 dark:bg-navy-700">
            <div
              className={`h-2.5 rounded-full ${getInterpretationColor(
                scores.D.t_score
              ).replace("text-", "bg-")}`}
              style={{ width: `${getProgressWidth(scores.D.t_score)}%` }}
            ></div>
          </div>

          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            <p>{interpretations.D}</p>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-8 border-t border-gray-200 pt-4 dark:border-gray-700">
        <h5 className="text-md mb-3 font-bold text-navy-700 dark:text-white">
          Interpretación de Puntuaciones T
        </h5>
        <div className="space-y-2">
          <div className="flex items-center">
            <div className="mr-2 h-3 w-3 rounded-full bg-red-500"></div>
            <span className="text-sm">
              <span className="font-medium">20 - 45:</span> Nivel bajo
            </span>
          </div>
          <div className="flex items-center">
            <div className="mr-2 h-3 w-3 rounded-full bg-yellow-500"></div>
            <span className="text-sm">
              <span className="font-medium">46 - 55:</span> Nivel promedio
            </span>
          </div>
          <div className="flex items-center">
            <div className="mr-2 h-3 w-3 rounded-full bg-green-500"></div>
            <span className="text-sm">
              <span className="font-medium">56 - 80:</span> Nivel alto
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BigFiveScoreDisplay;
